import axios, { AxiosError } from 'axios';
import { config } from 'config';
import { getAuthToken, getCompanyContext } from './apiClient';

const API_BASE_URL = config.apiUrl;

/**
 * Dedicated axios instance for fileService.
 *
 * Multi-tenant isolation: every request MUST include `X-Company-Context`
 * when an active company is set — contractor-file endpoints carry tenant-
 * scoped PII (identity docs, medical certs). The request interceptor here
 * injects both `Authorization` and `X-Company-Context` so individual
 * methods do not need to remember to set them (and cannot silently drop
 * the tenant header).
 *
 * Do NOT replace this instance with the bare `axios` default — it would
 * skip the interceptor and reintroduce the bypass.
 */
const fileAxios = axios.create();

fileAxios.interceptors.request.use(reqConfig => {
  const token = getAuthToken();
  if (token && !reqConfig.headers?.Authorization) {
    reqConfig.headers = reqConfig.headers || {};
    (reqConfig.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  const companyId = getCompanyContext();
  if (companyId) {
    reqConfig.headers = reqConfig.headers || {};
    (reqConfig.headers as Record<string, string>)['X-Company-Context'] = companyId;
  }
  return reqConfig;
});

// Valid document types for contractor files
const VALID_DOCUMENT_TYPES = ['identity', 'legal', 'medical', 'course'] as const;
type DocumentType = typeof VALID_DOCUMENT_TYPES[number];

// Custom error class for document access requirement
export class DocumentAccessRequiredError extends Error {
  code: string;
  requiresDocumentAuth: boolean;
  expiredAt?: string;

  constructor(message: string, code: string, expiredAt?: string) {
    super(message);
    this.name = 'DocumentAccessRequiredError';
    this.code = code;
    this.requiresDocumentAuth = true;
    this.expiredAt = expiredAt;
  }
}

// Helper to check if an error response requires document access
const isDocumentAccessRequired = (error: AxiosError<any>): boolean => {
  return error.response?.status === 403 && error.response?.data?.requiresDocumentAuth === true;
};

// Validate document type to prevent URL injection
const isValidDocumentType = (type: string): type is DocumentType => {
  return VALID_DOCUMENT_TYPES.includes(type as DocumentType);
};

export interface FileUploadResponse {
  success: boolean;
  fileId: string;
  filename: string;
  originalName: string;
  message?: string;
}

export interface FileInfo {
  _id: string;
  originalName: string;
  uploadedBy: string;
  uploadDate: string;
  fileType: string;
  bucketName: string;
}

class FileService {
  private apiUrl = `${API_BASE_URL}/files`;
  private contractorFilesUrl = `${API_BASE_URL}/contractor-files`;

  async uploadFile(file: File, userId?: string): Promise<FileUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      if (userId) {
        formData.append('uploadedBy', userId);
      }

      const response = await fileAxios.post(`${this.apiUrl}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 segundos para subida
      });

      // Handle different response formats.
      const data = response.data;

      // Current BE shape: { success, message, data: { id, filename, originalName } }.
      // Without this branch the caller reads `uploadResponse.fileId` →
      // undefined → falls through to "Debe seleccionar un documento" even
      // though the file uploaded fine.
      if (data?.data?.id) {
        return {
          success: true,
          fileId: data.data.id,
          filename: data.data.filename,
          originalName: data.data.originalName,
          message: data.message,
        };
      }

      // Legacy/alternate shape: { file: { id, filename, originalName } }.
      if (data?.file?.id) {
        return {
          success: true,
          fileId: data.file.id,
          filename: data.file.filename,
          originalName: data.file.originalName,
          message: data.message,
        };
      }

      // Otherwise return as is — callers downstream check `success`/`fileId`.
      return data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data) {
        return {
          success: false,
          fileId: '',
          filename: '',
          originalName: file.name,
          message: error.response.data.message || 'Error al subir archivo'
        };
      }
      throw error;
    }
  }

  async uploadMultipleFiles(files: File[], userId?: string): Promise<FileUploadResponse[]> {
    try {
      const uploads = files.map(file => this.uploadFile(file, userId));
      return await Promise.all(uploads);
    } catch (error: unknown) {
      throw error;
    }
  }

  async getFile(fileId: string): Promise<Blob> {
    const response = await fileAxios.get(`${this.apiUrl}/${fileId}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async getFileInfo(fileId: string): Promise<FileInfo> {
    const response = await fileAxios.get(`${this.apiUrl}/${fileId}/info`);
    return response.data;
  }

  async deleteFile(fileId: string): Promise<{ success: boolean; message: string }> {
    const response = await fileAxios.delete(`${this.apiUrl}/${fileId}`);
    return response.data;
  }

  // Métodos específicos para archivos de contratistas
  async uploadContractorIdentity(userId: string, files: {
    selfie?: File;
    idFront?: File;
    idBack?: File;
  }): Promise<FileUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('userId', userId);
      
      if (files.selfie) formData.append('selfie', files.selfie);
      if (files.idFront) formData.append('idFront', files.idFront);
      if (files.idBack) formData.append('idBack', files.idBack);

      const response = await fileAxios.post(`${this.contractorFilesUrl}/identity`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000,
      });

      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data) {
        return {
          success: false,
          fileId: '',
          filename: '',
          originalName: '',
          message: error.response.data.message || 'Error al subir fotos de identidad'
        };
      }
      throw error;
    }
  }

  async uploadContractorDocuments(userId: string, documents: {
    polizaINS?: File;
    ordenPatronal?: File;
    initialCourses?: File[];
    additionalCourses?: File[];
    medicalCertificate?: File;
    contractorLicense?: File;
    backgroundCheck?: File;
  }): Promise<FileUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('userId', userId);
      
      // Documentos individuales
      if (documents.polizaINS) formData.append('polizaINS', documents.polizaINS);
      if (documents.ordenPatronal) formData.append('ordenPatronal', documents.ordenPatronal);
      if (documents.medicalCertificate) formData.append('medicalCertificate', documents.medicalCertificate);
      if (documents.contractorLicense) formData.append('contractorLicense', documents.contractorLicense);
      if (documents.backgroundCheck) formData.append('backgroundCheck', documents.backgroundCheck);
      
      // Arrays de cursos
      if (documents.initialCourses) {
        documents.initialCourses.forEach(file => {
          formData.append('initialCourses', file);
        });
      }
      if (documents.additionalCourses) {
        documents.additionalCourses.forEach(file => {
          formData.append('additionalCourses', file);
        });
      }

      const response = await fileAxios.post(`${this.contractorFilesUrl}/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000,
      });

      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data) {
        return {
          success: false,
          fileId: '',
          filename: '',
          originalName: '',
          message: error.response.data.message || 'Error al subir documentos'
        };
      }
      throw error;
    }
  }

  async getContractorFiles(userId: string, documentType?: string): Promise<any> {
    let url = `${this.contractorFilesUrl}/user/${userId}`;

    // Validate and add documentType parameter if specified
    if (documentType && isValidDocumentType(documentType)) {
      url += `?documentType=${encodeURIComponent(documentType)}`;
    }

    try {
      const response = await fileAxios.get(url);
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && isDocumentAccessRequired(error)) {
        const data = error.response?.data;
        throw new DocumentAccessRequiredError(
          data?.error || 'Se requiere verificación de acceso al documento',
          data?.code || 'DOCUMENT_ACCESS_REQUIRED',
          data?.expiredAt
        );
      }
      throw error;
    }
  }

  // Métodos de conveniencia para tipos específicos de documentos
  async getIdentityFiles(userId: string): Promise<any> {
    return this.getContractorFiles(userId, 'identity');
  }

  async getLegalFiles(userId: string): Promise<any> {
    return this.getContractorFiles(userId, 'legal');
  }

  async getMedicalFiles(userId: string): Promise<any> {
    return this.getContractorFiles(userId, 'medical');
  }

  async getCourseFiles(userId: string): Promise<any> {
    return this.getContractorFiles(userId, 'course');
  }

  async downloadContractorFile(fileId: string): Promise<Blob> {
    const url = `${this.contractorFilesUrl}/download/${fileId}`;

    try {
      const response = await fileAxios.get(url, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && isDocumentAccessRequired(error)) {
        // For blob responses, error data might be a blob - try to parse it
        let errorData = error.response?.data;
        if (errorData instanceof Blob) {
          try {
            const text = await errorData.text();
            errorData = JSON.parse(text);
          } catch {
            // If parsing fails, use default error
            errorData = { error: 'Se requiere verificación de acceso al documento', code: 'DOCUMENT_ACCESS_REQUIRED' };
          }
        }
        throw new DocumentAccessRequiredError(
          errorData?.error || 'Se requiere verificación de acceso al documento',
          errorData?.code || 'DOCUMENT_ACCESS_REQUIRED',
          errorData?.expiredAt
        );
      }
      throw error;
    }
  }

  async deleteContractorFile(fileId: string): Promise<{ success: boolean; message: string }> {
    const response = await fileAxios.delete(`${this.contractorFilesUrl}/${fileId}`);
    return response.data;
  }

  async updateContractorDocument(
    userId: string, 
    fieldName: string, 
    file: File, 
    expiryDate?: string
  ): Promise<{ success: boolean; message: string; file?: any }> {
    try {
      const formData = new FormData();
      // The field name should be 'document' according to the CURL example
      formData.append('document', file);
      
      if (expiryDate) {
        formData.append('expiryDate', expiryDate);
      }

      // Use /user/ instead of /document/ in the URL path
      const response = await fileAxios.put(
        `${this.contractorFilesUrl}/user/${userId}/${fieldName}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 30000,
        }
      );

      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data) {
        return {
          success: false,
          message: error.response.data.message || 'Error al actualizar documento'
        };
      }
      throw error;
    }
  }
}

export const fileService = new FileService();
export default fileService;